// Browserify側のpolyfillを追えていないので念の為残しておく
import 'es6-object-assign/auto';
import Vue from 'vue';

import isPlainObject from 'latest_modules/lodash/isPlainObject';
import isArray from 'latest_modules/lodash/isArray';
import cloneDeep from 'latest_modules/lodash/cloneDeep';
import get from 'latest_modules/lodash/get';
import set from 'latest_modules/lodash/set';
import filter from 'latest_modules/lodash/filter';
import find from 'latest_modules/lodash/find';

const getFunctionName = function(fn) {
  if (fn.name) {
    return fn.name;
  }
  return (fn
    .toString()
    .trim()
    .match(/^function\s*([^\s(]+)/) || [])[1];
};

const isType = function(type, val) {
  if (val === undefined || val === null) {
    return false;
  }
  const valType = Object.prototype.toString.call(val).slice(8, -1);
  return valType === type;
};

/*
 * Define
 */
const SCHEMA_MYSELF = 'schema_myself';
const DEFAULTS = {
  ID_ATTRIBUTE: '_id'
};

/*
Schema Type
*/
class SchemaType {
  constructor(options = {}) {
    this.type = null;
    this.default = undefined;
    this.enum = undefined;
    this.nested = false;
    this.hasMany = false;
    this.ref = undefined;

    this.type = options.type ?
      getFunctionName(options.type) :
      getFunctionName(options);
    if (!this.type) {
      this.type = null;
    }

    if (options.default !== undefined) {
      if (this.type === 'Array') {
        this.default = [].concat(options.default);
      } else if (this.type === 'Object') {
        this.default = Object.assign({}, options.default);
      } else {
        this.default = options.default;
      }
    }

    if (options.enum !== undefined && isArray(options.enum)) {
      this.enum = options.enum;
    }
    this.nested = options.nested || false;

    this.hasMany = options.hasMany || false;

    if (this.hasMany && !isArray(this.default)) {
      this.default = [];
    }

    if (options.ref) {
      this.ref = options.ref;
    }
  }

  toValue(value) {
    if (this.hasMany) {
      if (isArray(value)) {
        return value.map((o) => this._toValue(o));
      }
      return undefined;
    }

    return this._toValue(value);
  }

  _toValue(value) {
    switch (this.type) {
      case 'Date':
        value = new Date(value);
        if (value.toString() === 'Invalid Date') {
          value = undefined;
        }
        break;

      case 'Boolean':
        switch (value) {
          case 'true':
            value = true;
            break;
          case 'false':
            value = false;
            break;
          default:
            value = Boolean(value);
        }
        break;

      case 'Number':
        value = Number(value);
        break;

      case 'String':
        value = String(value);
        if (this.enum && isArray(this.enum)) {
          if (this.enum.indexOf(value) < 0) {
            value = undefined;
          }
        }
        break;
    }

    // Array.from は ES6 からしか使用できないのでコメントアウト
    // when 'Array'
    //  value = Array.from value

    return value;
  }

  validate(value) {
    if (this.hasMany) {
      if (!isArray(value)) {
        return false;
      }
      return value.every((val) => {
        return isType(this.type, val);
      });
    }
    return isType(this.type, value);
  }
}

const documentFactory = function(schema) {
  class Document {
    constructor(values) {
      this.$_paths = {};
      this.$_values = {};
      if (schema) {
        this.add(schema);
      }
      this.setValues(values);
      this.toObject();
    }

    /*
     * add key path and settings schema type
     * Example:
     *   var Proucts = new Schema
     *   Proucts.add({ name: String, color: String, price: Number })
     *
     * @param {Object} schema
     * @param {String} prefix
     */
    add(schema, prefix = '') {
      for (let key in schema) {
        let prop = schema[key];
        if (isPlainObject(prop) && (!prop.type || isPlainObject(prop.type))) {
          this.path(`${prefix}${key}`, {
            type: Object,
            nested: true,
            default: {}
          });
          this.add(prop, `${prefix}${key}.`);
        } else if (isArray(prop)) {
          prop = prop[0];
          if (isPlainObject(prop)) {
            if (prop.type) {
              prop.hasMany = true;
              this.path(`${prefix}${key}`, prop);
            } else {
              this.path(`${prefix}${key}`, {
                hasMany: true,
                nested: true
              });
              this.add(prop, `${prefix}${key}.`);
            }
          }
        } else {
          this.path(`${prefix}${key}`, prop);
        }
      }
    }

    /*
     * Get/set a schema path
     *
     * @param {String} key
     * @param {String} obj Schema type
     */
    path(key, obj) {
      if (!obj) {
        if (/\[\d+\]/.test(key)) {
          key = key.replace(/\[\d+\]/, '');
        }
        return this.$_paths[key];
      }

      const path = new SchemaType(obj);

      if (key.indexOf('.') < 0) {
        Object.defineProperty(this, key, {
          enumerable: true,
          configurable: true,
          get: () => this.get(key),
          set: (value) => {
            this.set(key, value);
          }
        });
      }

      this.$_paths[key] = path;
    }

    /*
     * Set values
     *
     * @param {Object} values
     * @param {String} prefix
     */
    setValues(values, prefix) {
      if (prefix == null) {
        prefix = '';
      }
      for (let key in values) {
        const value = values[key];
        let path = `${prefix}${key}`;
        const pathObj = this.path(path);
        if (
          isPlainObject(value) &&
          (!pathObj ||
            (pathObj && (pathObj.nested || pathObj.type !== 'Object')))
        ) {
          this.setValues(value, `${path}.`);
        } else if (isArray(value) && pathObj && pathObj.nested) {
          value.map((val, index) => this.setValues(val, `${path}[${index}].`));
        } else {
          this.set(path, value);
        }
      }
    }

    /*
     * Get value (Getter define property)
     * @param {String} path
     */
    get(path) {
      const properties = this.path(path);
      return get(this.$_values, path) || (properties && properties.default);
    }

    /*
     * Set value (Setter define property)
     * @param {String} path
     * @param {any} value
     */
    set(path, value) {
      const pathObj = this.path(path);
      if (!pathObj) {
        return;
      }

      if (value === null) {
        return set(this.$_values, `${path}`, null);
      }

      value = pathObj.toValue(value);
      if (pathObj.validate(value)) {
        return set(this.$_values, `${path}`, value);
      }

      if (pathObj.default) {
        return set(this.$_values, `${path}`, pathObj.default);
      }
    }

    toObject(applyDefaults = true) {
      const defaultObj = {};
      if (applyDefaults) {
        for (let key in this.$_paths) {
          const path = this.$_paths[key];
          if (path.default !== undefined) {
            set(defaultObj, key, path.default);
          }
        }
      }

      return Object.assign(defaultObj, this.$_values);
    }
  }

  /*
   * Property name for ID (default: _id)
   */
  Document.idAttribute = DEFAULTS.ID_ATTRIBUTE;

  /*
   * Collection state
   */
  Document.state = {
    all: {},
    allList: []
  };

  /*
   * Getters
   */
  Document.getters = {
    list(state) {
      return state.allList.map((id) => state.all[id]);
    },
    find(state) {
      return function(where, options) {
        if (options == null) {
          options = {};
        }
        return Document.find(where, options, state);
      };
    },
    findOne(state) {
      return function(where, options) {
        if (options == null) {
          options = {};
        }
        return Document.findOne(where, options, state);
      };
    },
    findById(state) {
      return function(id, options) {
        if (options == null) {
          options = {};
        }
        return Document.findById(id, options, state);
      };
    }
  };

  /*
   * find()
   * Example:
   *   Posts.find({ title: 'foo' })
   *   Users.find(function(o) { o.age < 20 })
   *   Users.find('enabled')
   * refs: lodash.filter
   * @param {any} where
   * @param {Object} Options
   * @param {Object} State
   * @return {Array} Returns the new filtered array
   */
  Document.find = function(where, options = {}, state = null) {
    const _state = state || this.state;
    const results = filter(this.getters.list(_state), where);
    if (results.length > 0) {
      if (options.populate) {
        return results.map((res) => Document.populate(res, options.populate));
      }
    }
    return results;
  };

  /*
   * findOne()
   * Example:
   *   Posts.findOne({ title: 'foo' })
   *   Users.findOne(function(o) { o.age < 20 })
   *   Users.findOne('enabled')
   * refs: lodash.filter
   * @param {any} where
   * @param {Object} Options
   * @param {Object} State
   * @return {Array} Returns the matched document
   */
  Document.findOne = function(where, options = {}, state = null) {
    const _state = state || this.state;
    const result = find(this.getters.list(_state), where);
    if (result) {
      if (options.populate) {
        return Document.populate(result, options.populate);
      }
    }
    return result;
  };

  /*
   * findById()
   * Example:
   *   Posts.findById('55ada20a9dd1ecb71b958f6f')
   *   Users.findById('5900658e2c636fb32df84143')
   * @param {String} ID
   * @param {Object} Options
   * @param {Object} State
   * @return {Object} Match a document
   */
  Document.findById = function(id, options = {}, state = null) {
    const _state = state || this.state;
    const result = _state.all[id];
    if (result) {
      if (options.populate) {
        return Document.populate(result, options.populate);
      }
    }
    return result;
  };

  /*
   * populate()
   * @param {Object} result
   * @param {Array} populates populate properties
   * @return {Object} A populated document
   */
  Document.populate = function(result, populates) {
    let resultClone;
    if (!((populates && populates.length) > 0)) {
      return result;
    }

    if (result instanceof this) {
      resultClone = cloneDeep(result.toObject());
    } else {
      resultClone = cloneDeep(result);
    }
    const doc = new Document(resultClone);
    populates = isArray(populates) ? populates : [populates];

    return populates.reduce(
      function(resultClone, path) {
        let ReferenceDocument;
        const pathObj = doc.path(path);
        if (path.indexOf('.') >= 0) {
          const parentPath = path.split('.')[0];
          const parent = doc.path(parentPath);
          ReferenceDocument = parent.ref;
          if (ReferenceDocument === SCHEMA_MYSELF) {
            ReferenceDocument = Document;
          }
          if (
            typeof ReferenceDocument === 'string' &&
            Document.dependencyDocuments[ReferenceDocument]
          ) {
            ReferenceDocument = Document.dependencyDocuments[ReferenceDocument];
          }
          if (!parent.populated) {
            resultClone = Document.populate(resultClone, parentPath);
          }

          if (doc.get(parentPath)) {
            if (parent.hasMany) {
              resultClone[parentPath] = resultClone[parentPath].map((child) =>
                ReferenceDocument.populate(
                  child,
                  path.substring(path.indexOf('.') + 1)
                )
              );
            } else {
              resultClone[parentPath] = ReferenceDocument.populate(
                resultClone[parentPath],
                path.substring(path.indexOf('.') + 1)
              );
            }
          }

          return resultClone;
        }

        if (!(pathObj && pathObj.ref) || !doc.get(path)) {
          pathObj.populated = true;
          return resultClone;
        }
        // populate済みかどうか
        if (pathObj.populated) {
          return resultClone;
        }

        ReferenceDocument = pathObj.ref;
        if (ReferenceDocument === SCHEMA_MYSELF) {
          ReferenceDocument = Document;
        }

        if (
          typeof ReferenceDocument === 'string' &&
          Document.dependencyDocuments[ReferenceDocument]
        ) {
          ReferenceDocument = Document.dependencyDocuments[ReferenceDocument];
        }

        if (getFunctionName(ReferenceDocument) !== getFunctionName(Document)) {
          return new Error('It is an invalid reference');
        }
        if (pathObj.hasMany) {
          doc.get(path).map(function(refId, index) {
            const refRes = ReferenceDocument.findById(refId);
            set(resultClone, `${path}[${index}]`, refRes);
            return resultClone;
          })[0];
        } else {
          const refRes = ReferenceDocument.findById(doc.get(path));
          set(resultClone, path, refRes);
        }
        pathObj.populated = true;
        return resultClone;
      },

      resultClone
    );
  };

  /*
   * insert()
   * Example:
   *   Posts.insert({ _id: 'xxx', title: 'foo', body: 'bar' }).then(function(doc) {
   *     // do something
   *   })
   * @param {Object} payload insert new document key/value
   * @return {Object} new document
   */
  Document.insert = function(payload) {
    try {
      const document = new this(payload);
      const id = document[this.idAttribute];

      if (this.getters.findById(this.state)(id)) {
        return Promise.reject(new Error('A duplicate key already exists.'));
      }

      Vue.set(this.state.all, id, document.toObject());
      this.state.allList.push(id);
      return Promise.resolve(document);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  /*
   * update()
   * Example:
   *   Posts.update({ title: 'hoge', body: 'huga' })
   * @param {Object} where query
   * @param {Object} payload modify document key/value
   * @return {Array} documents
   */
  Document.update = function(where, payload, options = {}) {
    try {
      const documents = this.getters.find(this.state)(where);
      if (!documents.length) {
        if (options.upsert) {
          return this.insert(payload);
        }
        return Promise.reject(new Error('No document found.'));
      }
      for (let document of documents) {
        const newDocument = new this(payload);
        const object = newDocument.toObject(false);
        for (let key in object) {
          const val = object[key];
          document[key] = val;
        }
      }
      return Promise.resolve(documents);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  /*
   * updateById()
   * Example:
   *   Posts.updateById('5900658e2c636fb32df84143', { title: 'hoge', body: 'huga' })
   * @param {Object} payload modify document key/value
   * @return {Object} document
   */
  Document.updateById = function(id, payload, options = {}) {
    try {
      const document = this.getters.findById(this.state)(id);
      if (!document) {
        if (options.upsert) {
          return this.insert(payload);
        }
        return Promise.reject(new Error('No document found.'));
      }
      const newDocument = new this(payload);
      const object = newDocument.toObject(false);
      for (let key in object) {
        const val = object[key];
        document[key] = val;
      }
      return Promise.resolve(document);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  /*
   * remove()
   * Example:
   *   Posts.remove({ title: 'test' })
   * @param {Object} where query
   */
  Document.remove = function(where) {
    try {
      const documents = this.getters.find(this.state)(where);
      for (let document of documents) {
        Vue.delete(
          this.state.allList,
          this.state.allList.indexOf(document[this.idAttribute])
        );
        Vue.delete(this.state.all, document[this.idAttribute]);
      }
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(err);
    }
  };

  /*
   * removeById()
   * Example:
   *   Posts.remove('5900658e2c636fb32df84143')
   * @param {String} id
   */
  Document.removeById = function(id) {
    try {
      const document = this.getters.findById(this.state)(id);
      if (!document) {
        return Promise.reject(new Error('No document found.'));
      }
      Vue.delete(this.state.allList, this.state.allList.indexOf(id));
      Vue.delete(this.state.all, id);
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(err);
    }
  };

  /**
   * 依存注入されたDocumentの名前とDocumentクラス
   * @type {Object.<string, Document>}
   */
  Document.dependencyDocuments = {};

  /**
   * 依存注入用setter
   *
   * @param {string} name
   * @param {Document} DependencyDocument
   */
  Document.setDependencyDocument = (name, DependencyDocument) => {
    if (getFunctionName(DependencyDocument) !== getFunctionName(Document)) {
      return new Error('It is an invalid Document');
    }

    if (Document.dependencyDocuments[name]) {
      return;
    }

    Document.dependencyDocuments[name] = DependencyDocument;
  };

  return Document;
};

/*
 * Schema constructor
 * @param {Object} schema the values to set
 * @return {Class} Document document class
 */
class Schema {
  constructor(schema) {
    return documentFactory(schema);
  }
}

Schema.MYSELF = SCHEMA_MYSELF;

export default Schema;
